import { VariantServices } from "../../types/types";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import Option from "@JohnLewisPartnership/jl-design-system/dist/components/option/Option";
import { toggleSelectedService } from "../../utils/services/toggleSelectedService";
import { mappedServices } from "@/utils/services/serviceMapper";
import { useSharedServices } from "@/utils/services/useSharedServices";
import { emitServices } from "@/utils/services/emitServices";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/IconButton";
import { InfoOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/InfoOutlined";
import { CloseOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/CloseOutlined";

import styles from "./ExtendedWarranties.module.scss";

type Props = {
  /**
   * Indicates if a product variant is available to order.
   */
  availableToOrder: boolean;
  /**
   * The services available for a product variant.
   */
  variantServices: VariantServices;
  /**
   * Analytics Handler for extended warranties
   */
  extendedWarrantiesAnalyticsHandler: (warrantyTitle: string) => void;
  /**
   * List of mandatory services dmsIds to override default behavior
   */
  mandatoryServicesOverride: string[];
};

export const ExtendedWarrantiesUI = ({
  availableToOrder,
  variantServices,
  extendedWarrantiesAnalyticsHandler,
  mandatoryServicesOverride,
}: Props) => {
  const [services, updateServices] = useSharedServices();

  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    if (services.length > 0) {
      resetSelectedServices();
    }
    updateServices(
      mappedServices(variantServices, {
        mandatoryServicesOverride,
      }),
    );
  }, [variantServices]);

  const resetSelectedServices = () => {
    updateServices((prevState) =>
      prevState.map((service) => {
        return { ...service, selected: false };
      }),
    );
    emitServices([]);
  };

  const handleTooltipCollapseClick = () => setShowInfo(false);
  const handleTooltipExpandClick = () => {
    setShowInfo(!showInfo);
    infoButtonRef.current?.focus();
  };
  const handleToggleSelectedServiceClick =
    (warrantyId: string, warrantyTitle: string) =>
    (e: ChangeEvent<HTMLFormElement>) => {
      updateServices((prev) => toggleSelectedService(warrantyId, prev));
      if (e.target?.checked) {
        extendedWarrantiesAnalyticsHandler(warrantyTitle);
      }
    };

  const warranties = services.filter(
    (service) => service.serviceType === "ExtendedWarranty",
  );

  if (!warranties.length) return null;

  return (
    <div
      data-testid="product:services-and-warranties"
      data-component="ExtendedWarranties"
    >
      <div
        className={styles.extendedWarrantiesContainer}
        data-testid="product:extended-warranties"
      >
        {warranties.map((warranty, index) => {
          return (
            <section key={`warranty:${index}`}>
              <h2
                data-testid="product:extended-warranties:title"
                className={styles.title}
              >
                {warranty.title}
              </h2>
              <div className={styles.warrantyItem}>
                <Option
                  type="checkbox"
                  isControlled={true}
                  className={styles.warrantyInfo}
                  id={warranty.title}
                  ariaLabel={warranty.purchaseDescription}
                  label={warranty.purchaseDescription}
                  checked={warranty.selected}
                  onClick={handleToggleSelectedServiceClick(
                    warranty.id,
                    warranty.title,
                  )}
                  name={warranty.title}
                  value={""}
                  additionalInfo={warranty.display}
                  disabled={!availableToOrder}
                />
                <IconButton
                  IconComponent={InfoOutlined}
                  ariaLabel={`${warranty.title} information`}
                  data-testid="product:extended-warranty:tooltip:button:expand"
                  className={styles.warrantyToolTip}
                  aria-expanded={showInfo}
                  aria-controls={`extended-warranty-tooltip-${index}`}
                  ref={infoButtonRef}
                  onClick={handleTooltipExpandClick}
                  variant="clear-light"
                />
              </div>

              <div
                id={`extended-warranty-tooltip-${index}`}
                data-testid="product:extended-warranty:tooltip"
                className={styles.infoBlock}
                hidden={!showInfo}
              >
                <IconButton
                  IconComponent={CloseOutlined}
                  ariaLabel={"Collapse tooltip"}
                  data-testid="product:extended-warranty:tooltip:button:collapse"
                  className={styles.closeButton}
                  onClick={handleTooltipCollapseClick}
                  variant="clear-light"
                />
                <div aria-live="polite">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `${warranty.tooltip}`,
                    }}
                  />
                </div>
              </div>

              <div
                data-testid="product:extended-warranty:detail"
                className={styles.policyDetail}
                dangerouslySetInnerHTML={{
                  __html: `${
                    warranty.selected
                      ? warranty.confirmation
                      : warranty.policyDetail
                  }`,
                }}
              />
            </section>
          );
        })}
      </div>
    </div>
  );
};
